import React from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Phone, User, Loader2 } from "lucide-react";

export interface ContactPhoneOption {
  id: string | number;
  name: string;
  phone: string;
  type?: string;
}

interface ContactSelectionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  dealName?: string;
  contacts: ContactPhoneOption[];
  onSelectContact: (contact: ContactPhoneOption) => void;
  isCalling: boolean;
}

export const ContactSelectionModal: React.FC<ContactSelectionModalProps> = ({
  open,
  onOpenChange,
  dealName,
  contacts,
  onSelectContact,
  isCalling,
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-md bg-card border-border shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <DialogHeader>
          <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center mb-1">
            <Phone className="w-5 h-5" />
          </div>
          <DialogTitle className="text-lg font-bold">
            Escolha o Contato para Ligar
          </DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            {dealName
              ? `Negócio: ${dealName}`
              : "Selecione o contato desejado para iniciar a chamada VoIP:"}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 py-3 max-h-[320px] overflow-y-auto">
          {contacts.map((contact, idx) => (
            <div
              key={`${contact.id}-${contact.phone}-${idx}`}
              className="flex items-center justify-between p-3 rounded-lg border border-border/80 hover:border-primary/50 hover:bg-muted/50 transition-colors group cursor-pointer"
              onClick={() => !isCalling && onSelectContact(contact)}
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-9 h-9 rounded-full bg-muted flex items-center justify-center text-muted-foreground font-semibold shrink-0 group-hover:bg-primary/10 group-hover:text-primary transition-colors">
                  <User className="w-4 h-4" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">
                    {contact.name}
                  </p>
                  <p className="text-xs text-muted-foreground flex items-center gap-1.5 font-mono">
                    <span>{contact.phone}</span>
                    {contact.type && (
                      <span className="text-[10px] px-1.5 py-0.2 rounded bg-muted text-muted-foreground uppercase">
                        {contact.type}
                      </span>
                    )}
                  </p>
                </div>
              </div>

              <Button
                size="sm"
                variant="outline"
                className="shrink-0 gap-1.5 text-primary border-primary/20 hover:bg-primary hover:text-primary-foreground group-hover:border-primary"
                disabled={isCalling}
                onClick={(e) => {
                  e.stopPropagation();
                  onSelectContact(contact);
                }}
              >
                {isCalling ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <>
                    <Phone className="w-3.5 h-3.5" />
                    <span>Ligar</span>
                  </>
                )}
              </Button>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};
